/**
 * ProposalCard (SR2 Phase B) — the FE half of the action confirmation contract
 * (TECH §21). The model proposes; THIS card is where the user confirms, and the
 * confirm click is the authenticated action that executes the mutation. Guarded
 * (Tier-2 / upload-seeded) proposals require retyping the short token shown on
 * the card. Expired proposals render disabled.
 */

import { useTranslation } from '@tonnie/locale';
import type { CoPilotActionProposal } from '@tonnie/types';
import { Button, cn } from '@tonnie/ui';
import { useState } from 'react';

interface IProposalCardProps {
  proposal: CoPilotActionProposal;
  onConfirm: (proposalId: string, typedToken?: string) => Promise<'success' | 'stale' | 'error'>;
  onDismiss: (proposalId: string) => void;
}

export function ProposalCard({ proposal, onConfirm, onDismiss }: IProposalCardProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<'stale' | 'error' | null>(null);

  const expired = new Date(proposal.expiresAt).getTime() < Date.now();
  const needsToken = !!proposal.typedConfirmationToken;
  const tokenOk = !needsToken || token.trim().toUpperCase() === proposal.typedConfirmationToken;
  const untrusted = proposal.sourceContext?.untrustedContent;

  const confirm = async () => {
    if (busy || expired || !tokenOk) return;
    setBusy(true);
    setOutcome(null);
    // Send the SAME normalized form the enable-check + the card display use
    // (trimmed + uppercased). The input is visually uppercased via CSS, so a
    // lowercase entry looked correct but was sent raw → server rejected it (400).
    const result = await onConfirm(
      proposal.proposalId,
      needsToken ? token.trim().toUpperCase() : undefined
    );
    if (result === 'stale' || result === 'error') {
      setOutcome(result);
      setBusy(false);
    }
    // On success the card is removed by the parent — no local reset needed.
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm',
        proposal.riskTier >= 2
          ? 'border-tone-amber-line bg-tone-amber-subtle'
          : 'border-border bg-muted/40'
      )}
      role="group"
      aria-label={t('copilot.proposal.aria')}
      // Stable anchors for the proposal→confirm contract (parity with the mobile
      // ProposalCard). E2E drives the guarded path: read the token off the card,
      // retype it, confirm — asserting the mutation actually executed.
      data-testid={`copilot-proposal-${proposal.proposalId}`}
    >
      <p className="font-medium text-foreground">{proposal.humanSummary}</p>

      {untrusted && (
        <p className="mt-1 text-xs text-tone-amber">{t('copilot.proposal.untrustedWarning')}</p>
      )}

      {expired ? (
        <p className="mt-2 text-xs text-muted-foreground">{t('copilot.proposal.expired')}</p>
      ) : (
        <>
          {needsToken && (
            <div className="mt-2">
              <label
                className="text-xs text-muted-foreground"
                htmlFor={`tok-${proposal.proposalId}`}
              >
                {t('copilot.proposal.typeToConfirm').replace(
                  '{{token}}',
                  proposal.typedConfirmationToken ?? ''
                )}
              </label>
              <input
                id={`tok-${proposal.proposalId}`}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm uppercase tracking-widest"
                autoComplete="off"
                aria-label={t('copilot.proposal.typeToConfirm').replace('{{token}}', '')}
                data-testid="copilot-proposal-token"
              />
            </div>
          )}

          {outcome === 'stale' && (
            <p className="mt-2 text-xs text-tone-amber">{t('copilot.proposal.stale')}</p>
          )}
          {outcome === 'error' && (
            <p className="mt-2 text-xs text-destructive">{t('copilot.proposal.error')}</p>
          )}

          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={confirm}
              disabled={busy || !tokenOk}
              data-testid="copilot-proposal-confirm"
            >
              {busy ? t('copilot.proposal.confirming') : t('copilot.proposal.confirm')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDismiss(proposal.proposalId)}
              disabled={busy}
              data-testid="copilot-proposal-dismiss"
            >
              {t('copilot.proposal.dismiss')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
